import { useQuery } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import { listStages } from '../../graphql/queries';

const ONE_HOUR = 1000 * 60 * 60;

interface Stage {
  id: string;
  orgID: string;
  title: string;
  description?: string;
  sequence?: number;
  categoryID?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  iconName?: string;
  iconType?: string;
  iconColor?: string;
  action?: string;
  actionPastTense?: string;
  measure?: string;
  measurePlural?: string;
  valueUnit?: string;
  valueUnitPlural?: string;
  countUnit?: string;
  countUnitPlural?: string;
  pinColor?: string;
  icon?: string;
  sendToCRM?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface UseListStagesOptions {
  categoryId?: string;
}

async function fetchAllStages(
  orgID: string,
  categoryId?: string
): Promise<Stage[]> {
  let nextToken: string | null = null;
  const all: Stage[] = [];

  const filter: Record<string, any> = {
    orgID: { eq: orgID },
    isDeleted: { ne: true },
    isActive: { eq: true },
  };

  if (categoryId) {
    filter.categoryID = { eq: categoryId };
  }

  do {
    const res: any = await API.graphql(
      graphqlOperation(listStages, {
        filter,
        limit: 1000,
        nextToken,
      })
    );

    const items = (res?.data?.listStages?.items ?? []).filter(Boolean);
    all.push(...items);
    nextToken = res?.data?.listStages?.nextToken ?? null;
  } while (nextToken);

  // Sort by sequence, then title
  all.sort((a, b) => {
    const seqA = a.sequence ?? Number.MAX_SAFE_INTEGER;
    const seqB = b.sequence ?? Number.MAX_SAFE_INTEGER;
    if (seqA !== seqB) return seqA - seqB;
    return (a.title ?? '').localeCompare(b.title ?? '');
  });

  return all;
}

export function useListStages(options?: UseListStagesOptions) {
  const orgID = (global as any)?.me?.orgID as string | undefined;
  const categoryId = options?.categoryId;

  return useQuery<Stage[], Error>({
    queryKey: ['stages', orgID, categoryId ?? 'all'],
    queryFn: () => fetchAllStages(orgID!, categoryId),
    enabled: !!orgID,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
}
